import yaml from 'js-yaml';
import { Book } from '../types/Book';
import { apiBookService } from './apiBookService';

const BOOK_DIRECTORIES = ['butterfly-mystery','red-cedar-incident','frankenstein','dr-jekyll-mr-hyde','memory-thief', 'midnight-choice', 'space-rebellion'];

// Feature flag to switch between file-based and API-based loading
const USE_API = import.meta.env.VITE_USE_API === 'true' || true; // Default to API mode

export async function loadBooks(): Promise<Book[]> {
  // Try API first, fallback to file-based loading
  if (USE_API) {
    try {
      const isApiAvailable = await apiBookService.isApiAvailable();
      if (isApiAvailable) {
        console.log('📚 Using API-based book loading');
        return await apiBookService.loadBooks();
      } else {
        console.warn('⚠️ API not available, falling back to file-based loading');
      }
    } catch (error) {
      console.warn('⚠️ API loading failed, falling back to file-based loading:', error);
    }
  }

  // Fallback to file-based loading
  console.log('📁 Using file-based book loading');
  const books: Book[] = [];
  
  for (const bookId of BOOK_DIRECTORIES) {
    try {
      const response = await fetch(`/content/books/${bookId}/book.yml`);
      if (!response.ok) {
        console.warn(`Failed to load book.yml for ${bookId}: ${response.statusText}`);
        continue;
      }
      
      const yamlText = await response.text();
      const bookData = yaml.load(yamlText) as any;
      
      const book: Book = {
        id: bookData.id,
        title: bookData.title,
        logline: bookData.logline,
        cover_image: bookData.cover_image,
        estimated_duration: bookData.estimated_duration,
        genre: bookData.genre,
        target_age: bookData.target_age,
        author: bookData.author,
        author_website: bookData.author_website,
        story_paths: bookData.story_paths,
        main_paths: bookData.main_paths,
        audio_trailer: bookData.audio_trailer,
        chapters: bookData.chapters || [],
        paths: bookData.paths || [],
        // Preview mode fields
        preview_mode: bookData.preview_mode,
        preview_chapters: bookData.preview_chapters,
        feedback_prompt: bookData.feedback_prompt
      };
      
      books.push(book);
    } catch (error) {
      console.error(`Error loading book ${bookId}:`, error);
    }
  }
  
  return books;
}

export async function loadBook(bookId: string): Promise<Book | null> {
  // Try API first, fallback to file-based loading
  if (USE_API) {
    try {
      const isApiAvailable = await apiBookService.isApiAvailable();
      if (isApiAvailable) {
        console.log(`📖 Using API-based book loading for ${bookId}`);
        return await apiBookService.loadBook(bookId);
      } else {
        console.warn('⚠️ API not available, falling back to file-based loading');
      }
    } catch (error) {
      console.warn(`⚠️ API loading failed for book ${bookId}, falling back to file-based loading:`, error);
    }
  }

  // Fallback to file-based loading
  console.log(`📁 Using file-based book loading for ${bookId}`);
  try {
    const response = await fetch(`/content/books/${bookId}/book.yml`);
    if (!response.ok) {
      console.warn(`Failed to load book.yml for ${bookId}: ${response.statusText}`);
      return null;
    }
    
    const yamlText = await response.text();
    const bookData = yaml.load(yamlText) as any;
    
    const book: Book = {
      id: bookData.id,
      title: bookData.title,
      logline: bookData.logline,
      cover_image: bookData.cover_image,
      estimated_duration: bookData.estimated_duration,
      genre: bookData.genre,
      target_age: bookData.target_age,
      author: bookData.author,
      author_website: bookData.author_website,
      story_paths: bookData.story_paths,
      main_paths: bookData.main_paths,
      audio_trailer: bookData.audio_trailer,
      chapters: bookData.chapters || [],
      paths: bookData.paths || [],
      // Preview mode fields
      preview_mode: bookData.preview_mode,
      preview_chapters: bookData.preview_chapters,
      feedback_prompt: bookData.feedback_prompt
    };
    
    return book;
  } catch (error) {
    console.error(`Error loading book ${bookId}:`, error);
    return null;
  }
}

export async function loadChapterContent(bookId: string, chapterFile: string): Promise<string | null> {
  // Try API first, fallback to file-based loading
  if (USE_API) {
    try {
      const isApiAvailable = await apiBookService.isApiAvailable();
      if (isApiAvailable) {
        console.log(`📄 Using API-based chapter content loading for ${bookId}/${chapterFile}`);
        return await apiBookService.loadChapterContent(bookId, chapterFile);
      } else {
        console.warn('⚠️ API not available, falling back to file-based loading');
      }
    } catch (error) {
      console.warn(`⚠️ API loading failed for chapter ${chapterFile}, falling back to file-based loading:`, error);
    }
  }

  // Fallback to file-based loading
  console.log(`📁 Using file-based chapter content loading for ${bookId}/${chapterFile}`);
  try {
    const response = await fetch(`/content/books/${bookId}/story/${chapterFile}`);
    if (!response.ok) {
      console.warn(`Failed to load chapter ${chapterFile} for ${bookId}: ${response.statusText}`);
      return null;
    }
    
    return await response.text();
  } catch (error) {
    console.error(`Error loading chapter ${chapterFile} for book ${bookId}:`, error);
    return null;
  }
}

export async function getChapterChoiceText(bookId: string, chapterFile: string): Promise<string> {
  // Try API first, fallback to file-based loading
  if (USE_API) {
    try {
      const isApiAvailable = await apiBookService.isApiAvailable();
      if (isApiAvailable) {
        return await apiBookService.getChapterChoiceText(bookId, chapterFile);
      }
    } catch (error) {
      console.warn(`⚠️ API failed for chapter choice text ${chapterFile}, falling back to file-based loading:`, error);
    }
  }

  // Fallback to file-based loading
  const content = await loadChapterContent(bookId, chapterFile);
  if (!content) return `Chapter ${chapterFile.replace('.md', '')}`;
  
  // Extract choice text or title from YAML frontmatter
  const frontmatterMatch = content.match(/^---\n([\s\S]*?)\n---/);
  if (frontmatterMatch) {
    try {
      const frontmatter = yaml.load(frontmatterMatch[1]) as any;
      // Use 'choice' field if available, otherwise fall back to 'title'
      return frontmatter.choice || frontmatter.title || `Chapter ${chapterFile.replace('.md', '')}`;
    } catch (error) {
      console.warn(`Failed to parse frontmatter for ${chapterFile}:`, error);
    }
  }
  
  return `Chapter ${chapterFile.replace('.md', '')}`;
}

export async function getChapterTitle(bookId: string, chapterFile: string): Promise<string> {
  // Try API first, fallback to file-based loading
  if (USE_API) {
    try {
      const isApiAvailable = await apiBookService.isApiAvailable();
      if (isApiAvailable) {
        return await apiBookService.getChapterTitle(bookId, chapterFile);
      }
    } catch (error) {
      console.warn(`⚠️ API failed for chapter title ${chapterFile}, falling back to file-based loading:`, error);
    }
  }

  // Fallback to file-based loading
  const content = await loadChapterContent(bookId, chapterFile);
  if (!content) return `Chapter ${chapterFile.replace('.md', '')}`;
  
  // Extract title from YAML frontmatter
  const frontmatterMatch = content.match(/^---\n([\s\S]*?)\n---/);
  if (frontmatterMatch) {
    try {
      const frontmatter = yaml.load(frontmatterMatch[1]) as any;
      // Use 'title' field specifically for chapter titles
      return frontmatter.title || `Chapter ${chapterFile.replace('.md', '')}`;
    } catch (error) {
      console.warn(`Failed to parse frontmatter for ${chapterFile}:`, error);
    }
  }
  
  return `Chapter ${chapterFile.replace('.md', '')}`;
}